import React, { useCallback, useState } from 'react';
import { Text } from '../../../../ui';
import {
  KoobButton,
  KoobDataTable,
  KoobInputSelect,
  KoobInputNumber,
} from '@koob/ui';
import { useApp, useSnack } from '../../../../hooks';
import Search from '../../../../components/Search';
import { useTranslation } from 'react-i18next';
import { useDebounce, useDeepCompareEffect } from 'react-use';
import { Stack } from '@koob/margaret';
import { useEffect } from 'react';
import {
  KoobLocationSearch,
  SEARCH_DEFAULT_TYPES,
  SEARCH_TYPES,
} from '@koob/search';
import { PER_PAGE_FOR_LIST } from 'constants';

const LOCATION_SEARCH_TYPES = {
  experience: [...SEARCH_DEFAULT_TYPES, SEARCH_TYPES.EXPERIENCE],
  template: [...SEARCH_DEFAULT_TYPES, SEARCH_TYPES.TEMPLATE],
};

export default function DistributionBrowser({
  browserDatas,
  setBrowerDatas,
  selectAllShownItems,
  globalMargin,
  setGlobalMargin,
  isGlobalMarginPercent,
  setIsGlobalMarginPercent,
  BrowserHeadings,
  dataTableDatas,
  browserDatasList,
  setFilters,
  setPage,
  filters,
  reload,
  formatBrowerData,
  kind,
  tabTitle,
  searcTranslate,
  isLoading,
  limitPerPage = PER_PAGE_FOR_LIST,
}) {
  const { t, i18n } = useTranslation('toConnectionRequest');
  const { notify } = useSnack();
  const { currentOrganizationId, selectedDistributionOrganization } = useApp();
  const [hasNextPage, setHasNextPage] = useState(true);
  const [search, setSearch] = useState(filters?.search ?? '');

  const updateFilterValue = useCallback(
    (property, value) => {
      setFilters((current) => ({
        ...current,
        [property]: value,
      }));
    },
    [setFilters],
  );

  // The typed search reaches the request 500ms after the last keystroke.
  useDebounce(
    () => {
      if ((filters?.search ?? '') !== search) {
        updateFilterValue('search', search);
      }
    },
    500,
    [search],
  );

  useDeepCompareEffect(() => {
    setPage(1);
    setHasNextPage(true);
    setBrowerDatas([]);
    reload();
  }, [filters]);

  useEffect(() => {
    const formattedItems = formatBrowerData(browserDatasList?.data);
    // Dedupe by id with a Map, not an array indexed by id: ids above 2^32 - 2
    // (e.g. 1000000000864) are not array indices, so they were silently
    // dropped and the table showed "No results found".
    const uniqueData = new Map();
    [...browserDatas, ...formattedItems].forEach((item) => {
      if (!item?.id) {
        return;
      }
      uniqueData.set(String(item.id), item);
    });

    setBrowerDatas([...uniqueData.values()]);
    setHasNextPage(browserDatasList?.data.length >= limitPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserDatasList, setHasNextPage, setBrowerDatas]);

  const handleLoadNextPage = () => {
    setPage((page) => page + 1);
  };

  const columns = (BrowserHeadings || []).map(({ slug, label, width }) => ({
    key: slug,
    label,
    width,
    render: (row) =>
      row?.[slug]?.render ? row[slug].render() : row?.[slug]?.value,
  }));

  const typeOptions = [
    { label: t('allExperiences'), value: undefined },
    { label: t('activity'), value: 'Activity' },
    { label: t('transfer'), value: 'Transfer' },
    { label: t('program'), value: 'Program' },
    { label: t('airTransfer'), value: 'AIR' },
    { label: t('railTransfer'), value: 'RAILS' },
    { label: t('landTransfer'), value: 'LAND' },
    { label: t('boatTransfer'), value: 'BOAT' },
  ];
  return (
    <div>
      <Text type="h3">{t(tabTitle)}</Text>

      <div className="my-4 rounded-2xl border border-solid border-k-gray bg-white p-5">
        <div className="mb-2 flex justify-between">
          <h6 className="font-medium">
            {t('globalMargin', { unit: isGlobalMarginPercent ? '(%)' : '' })}
          </h6>

          <div className="flex items-center space-x-1">
            <input
              type="checkbox"
              checked={isGlobalMarginPercent}
              onChange={() => setIsGlobalMarginPercent(!isGlobalMarginPercent)}
            />
            <p className="text-sm">{t('usePercentMargin')}</p>
          </div>
        </div>

        <KoobInputNumber
          min={0}
          value={globalMargin}
          emptyValue={0}
          onValueChange={(value) => setGlobalMargin(value ?? 0)}
        />
      </div>

      <div data-testid="experience-data-table">
        <KoobDataTable
          columns={columns}
          data={dataTableDatas || []}
          loading={isLoading}
          search={
            <Search
              name="search"
              placeholder={t('misc:searchByNameOrId')}
              value={search}
              onChange={setSearch}
            />
          }
          filters={
            <div className="w-72">
              <KoobLocationSearch
                placeholder={t('locationsSearch', { ns: searcTranslate })}
                types={LOCATION_SEARCH_TYPES[kind]}
                locale={i18n.language}
                fromOrganizationId={
                  selectedDistributionOrganization?.value ??
                  currentOrganizationId
                }
                value={filters?.location}
                onValueChange={(value) => updateFilterValue('location', value)}
                kinds={['country', 'city', kind]}
                shouldSearchAdditionalFields={false}
                // KD-3547 — a DMC distributes products it is still preparing, so
                // the picker lists every state, not just available.
                includeUnavailable
              />
            </div>
          }
          toolbarActions={
            <>
              <KoobButton
                variant="secondary"
                aria-label={t('misc:select')}
                onClick={() => {
                  if (
                    window.confirm(t('selectAllExperienceConfirmationMessage'))
                  ) {
                    selectAllShownItems(browserDatas);
                    notify(t('selectedAllExperiencesuccess'));
                  }
                }}
              >
                <i className="fa-light fa-eye" />
              </KoobButton>
              {kind === 'experience' && (
                <div className="w-48">
                  <KoobInputSelect
                    value={filters?.type?.value}
                    onValueChange={(value) => {
                      updateFilterValue(
                        'type',
                        typeOptions.find((option) => option.value === value),
                      );
                    }}
                    placeholder={t('allExperiences')}
                    options={typeOptions}
                  />
                </div>
              )}
            </>
          }
        />
        <Stack
          direction="column"
          size="full"
          alignX="center"
          gutterSize={1}
          className="mt-4"
        >
          {hasNextPage && (
            <KoobButton variant="secondary" onClick={handleLoadNextPage}>
              {t('misc:seeMore')}
            </KoobButton>
          )}
        </Stack>
      </div>
    </div>
  );
}
